/**
 * @file A JavaScript module which provides functions to interact with the user currently in session.
 * Old AMD key: `ac/user`
 */

import { getMeta } from '../../../../common/index.tsx';
import type { ConnectCallback } from '../../../../types.tsx';
import type {
	CallbackFnGetLocale,
	CallbackFnGetTimeZone,
	CallbackFnGetCurrentUser,
	CallbackFnGetUser,
} from './user.types.tsx';

/**
 * Retrieves the current user object containing the user's id and full name
 * @param callback the callback that handles the response
 * @example
 *
 * AP.user.getUser(function(user){
 *   console.log("user id", user.id);
 *   console.log("user key", user.key);
 *   console.log("user name", user.fullName);
 * });
 * @deprecated as per <a href="https://ecosystem.atlassian.net/browse/ACJS-935">ACJS-935</a>
 */
export const getUser = (callback: ConnectCallback<CallbackFnGetUser>) => {
	const data = callback._context.extension.options.user;
	const fullName =
		typeof data.uid === 'undefined' && typeof data.ukey === 'undefined'
			? null
			: getMeta('remote-user-fullname') || null;

	callback({ fullName, id: data.uid, key: data.ukey });
};

/**
 * This method retrieves the current user object containing the user's Atlassian Account ID.
 *
 * @param callback the callback that handles the response. A single parameter is
 * passed to the callback. This parameter is an object comprising an attribute "atlassianAccountId"
 * and an attribute "accountType" which could be `atlassian` for a regular Atlassian user account
 * or `customer` for a Jira Service Desk account representing an external service desk user.
 * @example
 *
 * AP.user.getCurrentUser(function(user) {
 *   console.log("The Atlassian Account ID is", user.atlassianAccountId);
 * });
 */
export const getCurrentUser = (callback: ConnectCallback<CallbackFnGetCurrentUser>) => {
	const atlassianAccountId = getMeta('atlassian-account-id') || null;
	const accountType = getMeta('account-type') || null; // JSD

	callback({ atlassianAccountId, accountType });
};

/**
 * Retrieve the current user's timezone. If there is no logged in user, the server timezone is returned.
 *
 * Please be aware that this field is under profile visibility controls and that a user may intend to hide this
 * field from the public. Because this API will provide unrestricted access to the current user's timezone you
 * should only use this API to establish context for the user. You should not present the user's timezone in a
 * way that can be seen by other users viewing the application.
 *
 * @param callback the callback that handles the response
 * @example
 *
 * AP.user.getTimeZone(function(timezone){
 *   alert(timezone);
 * });
 *
 */
export const getTimeZone = (callback: ConnectCallback<CallbackFnGetTimeZone>) => {
	callback(callback._context.extension.options.user.timeZone);
};

/**
 * Retrieve the user's locale used by the product.
 *
 * Please be aware that this field is under profile visibility controls and that a user may intend to hide this
 * field from the public. Because this API will provide unrestricted access to the current user's locale you
 * should only use this API to establish context for the user. You should not present the user's locale in a
 * way that can be seen by other users viewing the application.
 *
 * @param callback the callback that handles the response
 * @example
 *
 * AP.user.getLocale(function(locale){
 *   alert(locale);
 * });
 *
 */
export const getLocale = (callback: ConnectCallback<CallbackFnGetLocale>) => {
	const userLocale = getMeta('user-locale');
	callback(userLocale);
};
